import { useState } from "react";
import type { DailyStats } from "@statuspage/shared";

interface UptimeBarsProps {
  dailyStats: DailyStats[];
  days: 30 | 60 | 90;
  onDaysChange: (days: 30 | 60 | 90) => void;
}

function getBarColor(uptimePct: number | null): string {
  if (uptimePct === null) return "bg-gray-300 dark:bg-slate-600";
  if (uptimePct >= 99) return "bg-green-500 dark:bg-green-400";
  if (uptimePct >= 90) return "bg-yellow-500 dark:bg-yellow-400";
  return "bg-red-500 dark:bg-red-400";
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function formatTooltip(dateStr: string, uptimePct: number | null): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const date = d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const uptime = uptimePct !== null ? `${uptimePct.toFixed(1)}%` : "No data";
  return `${date}\nUptime: ${uptime}`;
}

export default function UptimeBars({ dailyStats, days, onDaysChange }: UptimeBarsProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const showTooltip = (el: HTMLElement, date: string, uptimePct: number | null) => {
    const rect = el.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      text: formatTooltip(date, uptimePct),
    });
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, date: string, uptimePct: number | null) => {
    showTooltip(e.currentTarget, date, uptimePct);
  };

  const handleMouseLeave = () => setTooltip(null);

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>, date: string, uptimePct: number | null) => {
    showTooltip(e.currentTarget, date, uptimePct);
  };

  const handleBlur = () => setTooltip(null);

  return (
    <div className="relative">
      {/* Days toggle */}
      <div className="mb-3 flex items-center gap-2" role="radiogroup" aria-label="Zeitraum auswählen">
        {([30, 60, 90] as const).map((d) => (
          <button
            key={d}
            onClick={() => onDaysChange(d)}
            className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
              days === d
                ? "bg-blue-600 text-white dark:bg-blue-500"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            }`}
            role="radio"
            aria-checked={days === d}
            aria-label={`${d} Tage`}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                if (days === 90) {
                  onDaysChange(60);
                } else if (days === 60) {
                  onDaysChange(30);
                }
                return;
              }

              if (event.key === "ArrowRight") {
                event.preventDefault();
                if (days === 30) {
                  onDaysChange(60);
                } else if (days === 60) {
                  onDaysChange(90);
                }
              }
            }}
          >
            {d} Tage
          </button>
        ))}
      </div>

      {/* Bars */}
      <div className="flex items-end gap-[2px] overflow-x-auto pb-1" role="img" aria-label="Uptime-Balkendiagramm">
        {dailyStats.map((stat) => (
          <div
            key={stat.date}
            className="relative flex-shrink-0"
            tabIndex={0}
            onMouseEnter={(e) => handleMouseEnter(e, stat.date, stat.uptime_pct)}
            onMouseLeave={handleMouseLeave}
            onFocus={(e) => handleFocus(e, stat.date, stat.uptime_pct)}
            onBlur={handleBlur}
          >
            <div
              className={`h-16 w-2.5 rounded-t sm:w-3 ${getBarColor(stat.uptime_pct)}`}
              style={{ minHeight: "4px" }}
              aria-label={`${formatDate(stat.date)}: ${stat.uptime_pct !== null ? `${stat.uptime_pct.toFixed(1)}%` : "Keine Daten"}`}
            />
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 whitespace-pre rounded bg-slate-900 px-2 py-1 text-xs text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
          style={{ left: tooltip.x, top: tooltip.y }}
          role="tooltip"
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
